import type { AttachmentContent, UploadedFile } from 'app/types/attachment';
import type { LessonItemServiceResponse } from 'app/types/lessonItem';
import AttachmentCard from 'core/modules/components/cards/AttachmentCard';
import { isImage } from 'core/utils/utils';
import { CONTENT_TYPES } from 'core/variables/constant';
import React from 'react';
import Button from '../../../../atoms/Button/Button';
import TextHtml from '../../../../atoms/TextHtml/TextHtml';
import IconBubble from '../../../../molecules/IconBubble/IconBubble';
import MaterialPreviewMessages from './MaterialPreviewMessages';
import useMaterialPreviewStudentSidebar from './useMaterialPreviewStudentSidebar';

type Material = Partial<Pick<LessonItemServiceResponse, 'icon' | 'typeName' | 'name' | 'description' | 'course_messages_guid' | 'content_type_guid'>>;

type MaterialPreviewStudentSidebarProps = {
  material: Material;
  onClose: () => void;
  referenceMaterial?: AttachmentContent | null;
  attachedFiles?: UploadedFile[];
  contentLang?: string;
  courseGuid: string;
};

function MaterialPreviewStudentSidebar({
  material,
  onClose,
  referenceMaterial,
  attachedFiles = [],
  contentLang,
  courseGuid,
}: MaterialPreviewStudentSidebarProps): JSX.Element {
  const { t, hasAttachments, handleShowVisor, description } = useMaterialPreviewStudentSidebar({
    description: material?.description,
    referenceMaterial,
    attachedFiles,
  });

  return (
    <div className="material-preview-student-sidebar">
      <div className="material-preview-student-sidebar__header">
        <div className="material-preview-student-sidebar__header-content">
          {material.icon && isImage(material.icon) ? (
            <div className="lesson-item-card__left-icon-img" style={{ backgroundImage: `url(${material.icon})` }} />
          ) : (
            <IconBubble name={material.icon} size="md" background="alpha" color="primary" />
          )}
          <div className="material-preview-student-sidebar__header-texts">
            <span className="lesson-element-item__type">{material.typeName}</span>
            <span className="lesson-element-item__name" lang={contentLang}>
              {material.name}
            </span>
          </div>
        </div>
        <Button ariaLabel={t('actions:Close')} variant="primary" leftIcon="close" onClick={onClose} />
      </div>

      {description && (
        // El bloque tiene el scroll acotado para no dejar sin altura al panel de comentarios, y por
        // eso es una región focalizable: un contenedor con scroll que no se puede alcanzar con el
        // teclado incumple WCAG 2.1.1 (regla axe `scrollable-region-focusable`). El `tabIndex` va en
        // el envoltorio porque `TextHtml` solo lo expone junto a `onClick`, que lo haría `role="button"`.
        <section
          aria-label={t('common:Description')}
          className="material-preview-student-sidebar__description"
          // S6845 pide que `tabIndex` solo esté en elementos interactivos, pero aquí choca con la regla axe
          // `scrollable-region-focusable`: un bloque con scroll propio que no se puede enfocar deja el
          // contenido fuera del alcance del teclado (WCAG 2.1.1). Prevalece la segunda.
          tabIndex={0} // NOSONAR
        >
          <TextHtml isAuthored isBlockFormatted lang={contentLang} text={description} />
        </section>
      )}

      {hasAttachments && (
        <div className="material-preview-student-sidebar__attachments">
          <AttachmentCard
            content={referenceMaterial}
            guid={referenceMaterial?.guid}
            title={referenceMaterial?.name}
            type={referenceMaterial?.content_type_guid}
            onDelete={null}
            onAddContent={null}
            headerInfo={t(material?.content_type_guid === CONTENT_TYPES.TEST ? 'common:Material adjunto en el quiz' : 'common:Material adjunto en la tarea')}
            style="link"
            onShowVisor={referenceMaterial ? handleShowVisor : null}
            uploadedFiles={attachedFiles}
          />
        </div>
      )}

      {material?.course_messages_guid && <MaterialPreviewMessages courseGuid={courseGuid} courseMessageGuid={material.course_messages_guid} t={t} />}
    </div>
  );
}

export default MaterialPreviewStudentSidebar;
